/**
 * The Huddles area's dialogs (core HuddlesViewModel), mounted once by the shell so the sidebar's "New huddle" menu and
 * the Huddles screen open the same ones: "Start a huddle" (people or a channel; with Chat off, or nobody picked, a link
 * huddle the people are invited to), "Your link is ready" (name, link, who can join) and "Schedule a huddle".
 */
import type { MeetingPolicy } from "@bundle/contracts";
import { localDateTime, Profile, Sidebar, type HuddleStartForm, type OpenTarget } from "@bundle/core";
import { Avatar, Button, Chip, ListRow, RadioGroup, Sheet, Stack, Text, TextField, useFollow, useHuddlesArea, useT, useTr, useWorkspace } from "@bundle/ui";
import { useMemo, useState } from "react";
import { DateField, TimeSelect } from "../screens/calendar/DateTimeFields.tsx";

const COPIED_MS = 2000;

type Open = (url: string, target?: OpenTarget) => void;

export function HuddlesDialogs({ open }: { open: Open }) {
  const area = useHuddlesArea();
  if (area === null) return null;
  return (
    <>
      {area.state.start === undefined ? null : <StartDialog open={open} />}
      {area.state.link === undefined ? null : <LinkDialog open={open} />}
    </>
  );
}

/** People and channels matching the query (members first; channels only with Chat on). */
function usePicks(form: HuddleStartForm | undefined, chatOn: boolean) {
  const ws = useWorkspace();
  const cache = ws.state.cache;
  return useMemo(() => {
    if (cache === undefined || form === undefined) return { people: [], channels: [] };
    const q = form.query.trim().toLowerCase();
    if (q === "") return { people: [], channels: [] };
    const people = Object.values(cache.members)
      .filter((m) => m.userId !== cache.meId && m.kind === "human" && m.deactivated !== true && !form.userIds.includes(m.userId))
      .filter((m) => m.displayName.toLowerCase().includes(q) || (m.fullName ?? "").toLowerCase().includes(q))
      .slice(0, 6);
    const channels = chatOn
      ? Object.values(cache.channels)
          .filter((c) => (c.kind === "public" || c.kind === "private") && cache.memberships[c.id] !== undefined && c.archived !== true && c.name.toLowerCase().includes(q))
          .slice(0, 4)
      : [];
    return { people, channels };
  }, [cache, form, chatOn]);
}

function StartDialog({ open }: { open: Open }) {
  const t = useT();
  const tr = useTr();
  const ws = useWorkspace();
  const follow = useFollow(open);
  const area = useHuddlesArea()!;
  const { vm, state } = area;
  const form = state.start!;
  const cache = ws.state.cache;
  const picks = usePicks(form, state.chatOn);
  const channel = form.channelId === undefined || cache === undefined ? undefined : cache.channels[form.channelId];
  const nobody = form.userIds.length === 0 && channel === undefined;
  return (
    <Sheet open onClose={() => vm.closeStart()} title={t("huddles.start.title")} closeLabel={t("common.close")} detents={["medium"]} testID="huddles-start-dialog">
      <Stack gap="3" paddingY="1">
        <TextField
          value={form.query}
          onChange={(q) => vm.setStartQuery(q)}
          placeholder={state.chatOn ? t("huddles.start.who") : t("huddles.start.whoPeople")}
          accessibilityLabel={state.chatOn ? t("huddles.start.who") : t("huddles.start.whoPeople")}
          leadingIcon="search"
          autoFocus
          testID="huddles-start-query"
        />
        {form.userIds.length > 0 || channel !== undefined ? (
          <Stack direction="horizontal" gap="1.5" wrap>
            {channel === undefined || cache === undefined ? null : (
              <Chip label={`# ${Sidebar.channelTitle(cache, channel.id)}`} onRemove={() => vm.setStartChannel(undefined)} testID="huddles-start-channel" />
            )}
            {form.userIds.map((u) => (
              <Chip key={u} label={cache?.members[u]?.displayName ?? u} onRemove={() => vm.toggleStartPerson(u)} testID={`huddles-start-person-${u}`} />
            ))}
          </Stack>
        ) : null}
        {picks.people.length + picks.channels.length > 0 ? (
          <Stack gap="0">
            {picks.channels.map((c) => (
              <ListRow key={c.id} title={`# ${c.name}`} density="regular" onPress={() => vm.setStartChannel(c.id)} testID={`huddles-start-pick-c-${c.id}`} />
            ))}
            {picks.people.map((m) => (
              <ListRow
                key={m.userId}
                title={m.displayName}
                density="regular"
                leading={<Avatar name={m.displayName} {...Profile.avatarLook(m)} size="avatar.sm" />}
                onPress={() => vm.toggleStartPerson(m.userId)}
                testID={`huddles-start-pick-${m.userId}`}
              />
            ))}
          </Stack>
        ) : null}
        <Text variant="footnote" color="text.secondary">
          {nobody ? t("huddles.start.nobody") : state.chatOn ? "" : t("huddles.start.chatOff")}
        </Text>
        {form.error === undefined ? null : (
          <Text variant="footnote" color="status.danger" testID="huddles-start-error">
            {tr(form.error)}
          </Text>
        )}
        <Stack direction="horizontal" justify="end" gap="2">
          <Button label={t("common.cancel")} onPress={() => vm.closeStart()} testID="huddles-start-cancel" />
          <Button label={t("huddles.start.action")} variant="primary" icon="huddle" loading={form.busy} onPress={() => void vm.startHuddle(t("huddles.untitled")).then(follow)} testID="huddles-start-go" />
        </Stack>
      </Stack>
    </Sheet>
  );
}

function LinkDialog({ open }: { open: Open }) {
  const t = useT();
  const tr = useTr();
  const ws = useWorkspace();
  const follow = useFollow(open);
  const area = useHuddlesArea()!;
  const { vm, state } = area;
  const form = state.link!;
  const created = form.created;
  const workspace = ws.state.cache?.workspace.name ?? "";
  const [copied, setCopied] = useState(false);
  const [inviteQuery, setInviteQuery] = useState("");
  const cache = ws.state.cache;
  const copy = (url: string) =>
    void navigator.clipboard.writeText(url).then(
      () => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), COPIED_MS);
      },
      () => undefined,
    );
  const policyOptions = state.policyChoices.map((p) => ({ value: p, label: policyName(p), description: policyHint(p) }));
  function policyName(p: MeetingPolicy): string {
    return p === "open" ? t("huddles.access.open") : p === "workspace" ? t("huddles.access.workspace", { workspace }) : t("huddles.access.ask");
  }
  function policyHint(p: MeetingPolicy): string {
    return p === "open" ? t("huddles.access.open.hint") : p === "workspace" ? t("huddles.access.workspace.hint") : t("huddles.access.ask.hint", { workspace });
  }
  const schedule = form.mode === "schedule" && created === undefined;
  const invitable =
    cache === undefined || inviteQuery.trim() === ""
      ? []
      : Object.values(cache.members)
          .filter((m) => m.userId !== cache.meId && m.kind === "human" && m.deactivated !== true && !form.invitees.includes(m.userId))
          .filter((m) => m.displayName.toLowerCase().includes(inviteQuery.trim().toLowerCase()))
          .slice(0, 5);
  const canSchedule = vm.linkRequest(t("huddles.untitled")) !== undefined;
  return (
    <Sheet
      open
      onClose={() => vm.closeLink()}
      title={schedule ? t("huddles.schedule.title") : created === undefined ? t("huddles.link.making") : t("huddles.link.ready")}
      closeLabel={t("common.close")}
      detents={["medium"]}
      testID="huddles-link-dialog"
    >
      <Stack gap="4" paddingY="1">
        {schedule || created !== undefined ? (
          <TextField
            label={t("huddles.link.name")}
            value={form.title}
            onChange={(title) => vm.editLink({ title })}
            placeholder={t("meeting.new.namePlaceholder")}
            autoFocus={schedule}
            testID="huddles-link-name"
          />
        ) : null}
        {schedule ? (
          <Stack direction="horizontal" gap="2" align="center" wrap>
            <DateField value={form.date} onChange={(date) => vm.editLink({ date })} testID="huddles-schedule-date" />
            <TimeSelect value={form.time} onChange={(time) => vm.editLink({ time })} label={t("huddles.schedule.time")} testID="huddles-schedule-time" />
          </Stack>
        ) : null}
        {schedule && (localDateTime(form.date, form.time) ?? 0) <= ws.vm.deps.clock.now() ? (
          <Text variant="footnote" color="status.danger" testID="huddles-schedule-past">
            {t("huddles.schedule.past")}
          </Text>
        ) : null}
        {created === undefined ? null : (
          <Stack direction="horizontal" gap="2" align="center" padding="2" radius="md" background="bg.sunken">
            <Stack grow>
              <Text mono selectable testID="huddles-link-url">
                {created.url.replace(/^https?:\/\//, "")}
              </Text>
            </Stack>
            <Button label={copied ? t("huddle.share.copied") : t("huddle.share.copy")} icon={copied ? "check" : "copy"} size="sm" onPress={() => copy(created.url)} testID="huddles-link-copy" />
          </Stack>
        )}
        {created === undefined && !schedule && form.error === undefined ? <Text color="text.secondary">{t("huddles.link.making")}</Text> : null}
        {schedule || created !== undefined ? (
          <Stack gap="1.5">
            <Text variant="footnote" weight="semibold" color="text.secondary">
              {t("huddles.access.label")}
            </Text>
            <RadioGroup<MeetingPolicy> value={(created ?? form).policy} onChange={(policy) => vm.editLink({ policy })} options={policyOptions} accessibilityLabel={t("huddles.access.label")} testID="huddles-link-policy" />
          </Stack>
        ) : null}
        {schedule ? (
          <Stack gap="1.5">
            <TextField
              label={t("huddles.schedule.invite", { workspace })}
              value={inviteQuery}
              onChange={setInviteQuery}
              leadingIcon="users"
              testID="huddles-schedule-invite"
            />
            {form.invitees.length > 0 ? (
              <Stack direction="horizontal" gap="1.5" wrap>
                {form.invitees.map((u) => (
                  <Chip key={u} label={cache?.members[u]?.displayName ?? u} onRemove={() => vm.toggleInvitee(u)} testID={`huddles-invitee-${u}`} />
                ))}
              </Stack>
            ) : null}
            {invitable.map((m) => (
              <ListRow
                key={m.userId}
                title={m.displayName}
                density="regular"
                leading={<Avatar name={m.displayName} {...Profile.avatarLook(m)} size="avatar.sm" />}
                onPress={() => {
                  vm.toggleInvitee(m.userId);
                  setInviteQuery("");
                }}
                testID={`huddles-invite-pick-${m.userId}`}
              />
            ))}
            {form.invitees.length > 0 ? (
              <Text variant="footnote" color="text.secondary">
                {t("huddles.schedule.notice")}
              </Text>
            ) : null}
          </Stack>
        ) : null}
        {created !== undefined && form.mode === "link" ? (
          <Text variant="footnote" color="text.secondary">
            {t("huddles.link.untilRevoked")}
          </Text>
        ) : null}
        {form.error === undefined ? null : (
          <Text variant="footnote" color="status.danger" testID="huddles-link-error">
            {tr(form.error)}
          </Text>
        )}
        <Stack direction="horizontal" justify="end" gap="2">
          {schedule ? (
            <>
              <Button label={t("common.cancel")} onPress={() => vm.closeLink()} testID="huddles-schedule-cancel" />
              <Button
                label={t("huddles.schedule.save")}
                variant="primary"
                icon="calendar"
                disabled={!canSchedule}
                loading={form.busy}
                onPress={() => void vm.createLink(t("huddles.untitled"))}
                testID="huddles-schedule-save"
              />
            </>
          ) : created === undefined && form.error !== undefined ? (
            <Button label={t("common.retry")} onPress={() => void vm.createLink(t("huddles.untitled"))} testID="huddles-link-retry" />
          ) : (
            <>
              <Button label={t("huddles.link.done")} onPress={() => vm.closeLink()} testID="huddles-link-done" />
              {created !== undefined && created.scheduledAt === undefined ? (
                <Button label={t("huddles.link.startNow")} variant="primary" icon="huddle" onPress={() => follow(vm.startLink())} testID="huddles-link-start" />
              ) : null}
            </>
          )}
        </Stack>
      </Stack>
    </Sheet>
  );
}
